import { ActivatedChallengeImage, ActivatedChallengePurchasesResponse, ActivatedMiscPointsActivity, ChallengeType, ChargebeeTransactionStatus, CheckinInfo, CreateChallengeCheckin, GetActivatedChallengeResponse, Location, ObjectId, UpdateActivatedChallengeRequest } from '../types';
import { GoogleTagEvent, logWithGoogleTag } from '../helpers/api/api';
import { LitElement, css, html } from 'lit';
import { add_challenge_admin_points, add_challenge_checkin, deleteActivatedChallenge, overrideActivatedChallengePayment, redeem_activated_challenge_reward, remove_activated_challenge_reward, remove_activity_checkin, remove_admin_points_from_activated_challenge, remove_challenge_checkin, resendChallengeActivationEmail, resetActivatedChallenge, set_challenge_photo_visibility, updateActivatedChallenge } from '../helpers/api/challenge-api';
import { customElement, property, query, state } from 'lit/decorators';
import { isDefined, undefinedIfEmpty } from '../helpers/utils';

import { DEFAULT_CATEGORY_ICON } from '../helpers/constants';
import { DatePicker } from '@vaadin/vaadin-date-picker';
import { Dialog } from '@material/mwc-dialog';
import { ProxiSelectfield } from './theme/proxi-selectfield';
import { StateController } from '../helpers/state/state-management/state-controller';
import { TimePicker } from '@vaadin/vaadin-time-picker';
import accountInfo from '../helpers/state/account-info';
import { areLocationsSame } from '../helpers/filters';
import challengeData from '../helpers/state/challenge-data';
import format from 'date-fns/format';
import { formatPrice } from '../helpers/api/products-api';
import { getMainPage } from '../helpers/page-name';
import pointCache from '../helpers/state/point-cache';
import topicData from '../helpers/state/topic-data';

@customElement('challenge-participant-details-modal')
export class ChallengeParticipantDetailsModal extends LitElement {
    static amplitude_callsite = 'ChallengeParticipantDetailsModal';

    @query('#confirmDialog')
    confirmDialog!: Dialog;

    @query('#resetDialog')
    resetDialog!: Dialog;

    @query('#bonusPointsValue')
    bonusPointsValue!: HTMLInputElement;

    @query('#bonusPointsDescription')
    bonusPointsDescription!: HTMLInputElement;

    @query('#manual_location')
    manualLocation?: ProxiSelectfield;

    @query('#overrideDate')
    public overrideDateElement!: DatePicker;

    @query('#overrideTime')
    public overrideTimeElement!: TimePicker;

    @property({ type: Object })
    data?: GetActivatedChallengeResponse;

    @state()
    activatedChallenge: GetActivatedChallengeResponse | undefined;

    @state()
    checkinsInfo: CheckinInfo[] = [];

    @state()
    savedPlacesInfo: Array<{
        categoryIcon: string;
        categoryName: string;
        placeName: string;
        location: Location | undefined;
        challenge_location_id: ObjectId | undefined;
    }> = [];

    @state()
    showBonusPoints = false;

    @state()
    showManualCheckin = false;

    @state()
    purchases?: ActivatedChallengePurchasesResponse;

    private readonly bindPointCacheState = new StateController(this, pointCache);
    private readonly bindTopicState = new StateController(this, topicData);
    private readonly bindAccountState = new StateController(this, accountInfo);
    private readonly bindChallengeDataState = new StateController(this, challengeData);

    async updated(changedProperties: any) {
        changedProperties.forEach((oldValue: any, propName: any) => {
            if(propName === 'data') {
                this.activatedChallenge = this.data;
                this.getCheckinsInfo();
                this.getSavedPlacesInfo();
                // eslint-disable-next-line @typescript-eslint/no-floating-promises
                this.getPurchasesInfo();
            }
        });
    }

    private async getPurchasesInfo() {
        this.purchases = await challengeData.getSpecificUserPurchasesAsAdmin(this.activatedChallenge?._id.$oid ?? '');
    }

    private getCheckinsInfo() {
        this.checkinsInfo = [];
        const challenge = topicData.allChallenges.find((c) => c._id.$oid === this.activatedChallenge?.challenge_id.$oid);
        if (!isDefined(challenge)) return;
        if (isDefined(this.activatedChallenge?.checkin_locations)) {
            this.activatedChallenge?.checkin_locations.forEach((checkin_location) => {

                const location = challenge.locations.find(
                    (l) => areLocationsSame(l.location, checkin_location)
                );

                const point = pointCache.allPoints.find(
                    (p) => areLocationsSame(p.location, checkin_location)
                );

                const category = topicData.categories.find((c) => c._id?.$oid === point?.topic_category_id.$oid);

                const timestamps = this.activatedChallenge?.checkins
                    ?.filter((c) => areLocationsSame(c.location, checkin_location))
                    .map((c) => c.timestamp)
                    .filter((t): t is string => isDefined(t) && typeof t === 'string') ?? [];

                const counted_timestamps = this.activatedChallenge?.counted_checkins
                    ?.filter((c) => location?._id?.$oid === c.location_id.$oid)
                    .map((c) => c.timestamp)
                    .filter((t): t is string => isDefined(t) && typeof t === 'string') ?? [];

                const receipts = this.activatedChallenge?.receipts?.filter((receipt) => receipt.challenge_location_id.$oid === location?._id?.$oid) ?? [];

                this.checkinsInfo.push({
                    all_timestamps: timestamps?.filter((t) => isDefined(t)) ?? [],
                    counted_timestamps: counted_timestamps?.filter((t) => isDefined(t)) ?? [],
                    categoryIcon: category?.icon ?? DEFAULT_CATEGORY_ICON,
                    categoryName: category?.name ?? '',
                    placeName: point?.name ?? point?.location.search ?? '',
                    pointValue: location?.value ?? 0,
                    location: location?.location,
                    challenge_location_id: location?._id,
                    receipts
                });
            });
        }
    }

    private getSavedPlacesInfo() {
        this.savedPlacesInfo = [];
        const challenge = topicData.allChallenges.find((c) => c._id.$oid === this.activatedChallenge?.challenge_id.$oid);
        if (!isDefined(challenge)) return;
        if (isDefined(this.activatedChallenge?.personal_locations)) {
            this.activatedChallenge?.personal_locations.saved.forEach((saved_location) => {

                const location = challengeData.challengeLocations.find(
                    (l) => l.point_info?._id.$oid === saved_location.$oid
                );

                const point = pointCache.allPoints.find(
                    (p) => areLocationsSame(p.location, location?.challenge_location_info?.location)
                );

                const category = topicData.categories.find((c) => c._id?.$oid === point?.topic_category_id.$oid);

                this.savedPlacesInfo.push({
                    categoryIcon: category?.icon ?? DEFAULT_CATEGORY_ICON,
                    categoryName: category?.name ?? '',
                    placeName: point?.name ?? point?.location.search ?? '',
                    location: location?.challenge_location_info?.location,
                    challenge_location_id: location?.challenge_location_info?._id,
                });
            });
        }
    }

    async hideOnLeaderboard() {
        if (!isDefined(this.activatedChallenge)) return;
        const request: UpdateActivatedChallengeRequest = {
            hidden_from_leaderboard: !this.activatedChallenge.hidden_from_leaderboard
        };
        await updateActivatedChallenge(this.activatedChallenge?._id.$oid, request, topicData.topicKey);
        this.dispatchEvent(
            new CustomEvent('on-save')
        );
        this.dispatchEvent(
            new CustomEvent('refresh-challenges')
        );
    }

    async toggleTesterStatus() {
        if (!isDefined(this.activatedChallenge)) return;
        if (!this.activatedChallenge.is_tester && !confirm('Are you sure you want to make this user a tester? They will have early access to participate in the challenge.')) return;
        const request: UpdateActivatedChallengeRequest = {
            is_tester:  !(this.activatedChallenge.is_tester ?? false)
        };
        await updateActivatedChallenge(this.activatedChallenge?._id.$oid, request, topicData.topicKey);
        this.dispatchEvent(
            new CustomEvent('on-save')
        );
    }

    async openResetConfirmDialog() {
        this.resetDialog.show();
    }

    async resetUser() {
        // confirm
        if (!isDefined(this.activatedChallenge)) return;
        await resetActivatedChallenge(this.activatedChallenge?._id.$oid, topicData.topicId, topicData.topicKey);
        this.resetDialog.close();
        this.dispatchEvent(
            new CustomEvent('on-save')
        );
        this.dispatchEvent(
            new CustomEvent('refresh-challenges')
        );
    }

    async overridePayment() {
        // confirm
        if (!isDefined(this.activatedChallenge)) return;
        await overrideActivatedChallengePayment(this.activatedChallenge?._id.$oid, topicData.topicId, topicData.topicKey);
        this.resetDialog.close();
        this.dispatchEvent(
            new CustomEvent('on-save')
        );
        this.dispatchEvent(
            new CustomEvent('refresh-challenges')
        );
    }

    async openRemoveConfirmDialog() {
        this.confirmDialog.show();
    }

    async removeUser() {
        !confirm('Are you sure you want to remove this user?') && this.confirmDialog.close();
        if (!isDefined(this.activatedChallenge) || !this.activatedChallenge.email) return;
        await deleteActivatedChallenge(ChallengeParticipantDetailsModal.amplitude_callsite, this.activatedChallenge._id.$oid);
        this.confirmDialog.close();
        this.dispatchEvent(
            new CustomEvent('on-save')
        );
        this.dispatchEvent(
            new CustomEvent('refresh-challenges')
        );
        getMainPage().errorToastElement.showToast('User removed from challenge');
    }

    async addBonusPoints() {
        const value = Number(this.bonusPointsValue?.value);
        const description = this.bonusPointsDescription?.value;
        if (!isDefined(this.activatedChallenge) || !value || !description) return;
        try {
            await add_challenge_admin_points(this.activatedChallenge?._id.$oid, value, description);
        } catch(e) {
            getMainPage().errorToastElement.showToastAndThrow('Error adding bonus points');
        }
        this.showBonusPoints = false;
        this.dispatchEvent(
            new CustomEvent('refresh-challenges')
        );

        this.requestUpdate();
    }

    async addManualCheckin() {
        if (!isDefined(this.activatedChallenge)) return;
        const location = this.manualLocation?.value;
        if (!isDefined(location)) return;
        const timestamp_day_string = this.overrideDateElement?.value;
        const timestamp_time_string = this.overrideTimeElement?.value;
        let timestamp: string;
        if (isDefined(timestamp_day_string) && isDefined(timestamp_time_string)) {
            const date = new Date(timestamp_day_string);
            const hours = Number(timestamp_time_string.split(':')[0]);
            const minutes = Number(timestamp_time_string.split(':')[1]);
            timestamp = new Date(date.getFullYear(), date.getMonth(), date.getDate(), hours, minutes).toISOString();
        } else {
            timestamp = new Date().toISOString();
        }

        // put into UTC format
        const utcDate = new Date(timestamp);
        const utcOffset = utcDate.getTimezoneOffset();
        const utcTimestamp = new Date(utcDate.getTime() - (utcOffset * 60 * 1000)).toISOString();

        // if it is before the start date or after the end date, show error
        if (challengeData.challenge && isDefined(challengeData.challenge?.start_time) && isDefined(challengeData.challenge?.end_time)) {
            const startDate = new Date(challengeData.challenge?.start_time);
            const endDate = new Date(challengeData.challenge?.end_time);
            if (utcDate < startDate || utcDate > endDate) {
                getMainPage().errorToastElement.showToast('OOPS - The date is not within the challenge dates. It will not count.');
                return;
            }
        }

        try {
            let checkinbody;
            if (undefinedIfEmpty(location)) {
                const location_check_in = {
                    LocationCheckIn: {
                        challenge_location_id: location,
                        code: '',
                        timestamp_override: utcTimestamp,
                    }
                };
                const CheckInData : CreateChallengeCheckin = {
                    method: location_check_in
                };
                checkinbody = CheckInData;
            } else {
                const code_check_in = {
                    CodeCheckIn: {
                        code: '',
                        timestamp_override: utcTimestamp,
                    }
                };
                const CheckInData : CreateChallengeCheckin = {
                    method: code_check_in
                };
                checkinbody = CheckInData;
            }
            if (isDefined(this.activatedChallenge)) {
                await add_challenge_checkin(checkinbody, this.activatedChallenge._id.$oid);
                this.showManualCheckin = false;
                this.dispatchEvent(
                    new CustomEvent('refresh-challenges')
                );
                this.requestUpdate();
                getMainPage().errorToastElement.showToast('Check-in added');

                logWithGoogleTag('Challenge Check In', GoogleTagEvent.USER_CHECKED_IN, {
                    topic_id: topicData.topic?._id?.$oid,
                    point_id: challengeData.challengeLocations.find((l) => l.point_info?._id.$oid === location)?.point_info?._id.$oid ?? '',
                    organization_id: topicData.topic?.organization_id?.$oid,
                    challenge_name: challengeData.challenge?.name ?? '',
                });

            }
        } catch (e) {
            getMainPage().errorToastElement.showToast('OOPS - The code is wrong, you need to use a QR code, or you are not within the required radius. Try again.');
        }
    }



    async resendWelcomeEmail() {
        if (!isDefined(this.activatedChallenge)) return;
        await resendChallengeActivationEmail(this.activatedChallenge?.challenge_id.$oid, this.activatedChallenge?.email, this.activatedChallenge?.phone_number);
        getMainPage().errorToastElement.showToast('Email sent');
    }


    async removeBonusPoints(bonus_point_id: string) {
        if (!isDefined(this.activatedChallenge)) return;
        if (!confirm('Are you sure you want to remove this bonus point?')) {
            return;
        }
        try {
            await remove_admin_points_from_activated_challenge(this.activatedChallenge?._id.$oid, bonus_point_id);
        } catch(e) {
            getMainPage().errorToastElement.showToastAndThrow('Error removing bonus points');
        }
        this.dispatchEvent(
            new CustomEvent('refresh-challenges')
        );
        this.requestUpdate();
    }

    async removeUserCheckin(checkin: CheckinInfo, timestamp?: string) {
        if (!isDefined(this.activatedChallenge)) return;
        if (!confirm('Are you sure you want to remove this check-in?')) {
            return;
        }
        const checkins = this.activatedChallenge.checkins?.filter((c) => {
            return areLocationsSame(c.location, checkin.location);
        });

        if (!isDefined(checkins) || checkins.length === 0) return;

        let checkin_id;

        // If timestamp is provided, filter by timestamp
        if (isDefined(timestamp)) {
            const checkinToRemove = checkins.find((c) => c.timestamp === timestamp);
            // Get the ID of the check-in to remove
            checkin_id = checkinToRemove?._id;
        } else {
            // If no timestamp is provided, remove the first check-in that matches the location
            const checkinToRemove = checkins[0];
            // Get the ID of the check-in to remove
            checkin_id = checkinToRemove?._id;
        }

        if (isDefined(checkin_id)) {
            await remove_challenge_checkin(this.activatedChallenge._id.$oid, checkin_id.$oid);
            this.getCheckinsInfo();
        }
        this.dispatchEvent(
            new CustomEvent('refresh-challenges')
        );
    }

    async removeUserActivity(activity: ActivatedMiscPointsActivity) {
        if (!isDefined(this.activatedChallenge)) return;
        if (!confirm('Are you sure you want to remove this activity?')) {
            return;
        }
        const activity_id = activity.activity_id.$oid;
        if (isDefined(activity_id)) {
            await remove_activity_checkin(this.activatedChallenge._id.$oid, activity_id);
            this.getCheckinsInfo();
        }

        this.dispatchEvent(
            new CustomEvent('refresh-challenges')
        );
    }

    get nowTime() {
        const date = new Date();
        const hours = date.getHours();
        const minutes = date.getMinutes();
        return `${hours}:${minutes}`;
    }

    get nowDate() {
        const date = new Date();
        const day = date.getDate();
        const month = date.getMonth() + 1;
        const year = date.getFullYear();
        return `${year}-${month}-${day}`;
    }

    async removeRedemption(reward_id: string) {
        if (!isDefined(this.activatedChallenge)) return;
        if (!confirm('Are you sure you want to remove this redemption? This cannot be undone.')) {
            return;
        }

        this.activatedChallenge = await remove_activated_challenge_reward(
            this.activatedChallenge._id.$oid,
            topicData.topicKey,            reward_id,
        );

        this.dispatchEvent(
            new CustomEvent('refresh-challenges')
        );
        getMainPage().errorToastElement.showToast('Redemption removed');
    }

    async redeemReward(reward_id: string) {
        if (!isDefined(this.activatedChallenge)) return;

        this.activatedChallenge = await redeem_activated_challenge_reward(
            this.activatedChallenge._id.$oid,
            topicData.topicKey,
            reward_id,
        );

        this.dispatchEvent(
            new CustomEvent('refresh-challenges')
        );
        getMainPage().errorToastElement.showToast('Reward marked as redeemed');
    }

    // Approved + pending photos for a given reward/check-in/activity location id.
    imagesForLocation(locationId?: string): ActivatedChallengeImage[] {
        if (!isDefined(locationId)) return [];
        return [
            ...(this.activatedChallenge?.images ?? []),
            ...(this.activatedChallenge?.pending_images ?? []),
        ].filter((img) => img.challenge_location_id.$oid === locationId);
    }

    async togglePhotoVisibility(image: ActivatedChallengeImage) {
        if (!isDefined(this.activatedChallenge)) return;
        this.activatedChallenge = await set_challenge_photo_visibility(
            this.activatedChallenge._id.$oid,
            image._id.$oid,
            !image.can_be_displayed,
        );
        this.dispatchEvent(
            new CustomEvent('refresh-challenges')
        );
        getMainPage().errorToastElement.showToast(
            !image.can_be_displayed ? 'Photo is now public' : 'Photo hidden from public'
        );
    }

    renderPhotoLink(image: ActivatedChallengeImage, label: string) {
        // The visibility toggle only applies to approved photos; pending ones aren't public yet.
        const isApproved = (this.activatedChallenge?.images ?? []).some((img) => img._id.$oid === image._id.$oid);
        return html`
            <span style="display: inline-flex; align-items: center; gap: 4px;">
                <a href="${image.image_url}" target="_blank" rel="noopener noreferrer">${label} ${isApproved ? (image.can_be_displayed ? '(Public)' : '(Private)') : '(Pending approval)'}</a>
                ${isApproved ? html`
                    <mwc-icon
                        title="${image.can_be_displayed ? 'Hide from public leaderboard & photo feed' : 'Show on public leaderboard & photo feed'}"
                        style="--mdc-icon-size: 16px; color: var(--main-moon-color); cursor: pointer;"
                        @click="${async () => await this.togglePhotoVisibility(image)}"
                    >${image.can_be_displayed ? 'visibility' : 'visibility_off'}</mwc-icon>
                ` : ''}
            </span>
        `;
    }

    /** A registration answer is stored on the participant with the field's
     *  label. When a field is later removed from the form, its answers linger
     *  (often blank, since registration stores an entry per field). Hide those
     *  so a deleted field stops showing here. Falls back to showing all if the
     *  challenge isn't loaded yet. */
    private isCurrentRegistrationField(fieldName: string): boolean {
        const current = challengeData.challenge?.registration_settings?.additional_fields;
        if (!current) return true;
        return current.some((f) => f.label === fieldName);
    }

    render() {
        return html`
            <div class="modalContent">
                <div class="modal__header">
                    Participant Details
                </div>
                <div class="modal__content">
                ${isDefined(this.activatedChallenge)
                    ? html`
                        <div class="participant-details">
                            <div class="participant">
                                <b>${this.activatedChallenge.display_name}</b> (${this.activatedChallenge.email})
                                ${this.activatedChallenge.additional_fields
                                    ?.filter((field) => this.isCurrentRegistrationField(field.field_name))
                                    .map((field) => html`
                                    <div>${field.field_name}: ${field.field_value}</div>
                                `)}
                            </div>
                            <div class="stats-card-container">
                                <div class="stats-card half">
                                    <div class="stats-card__value">${this.activatedChallenge.checkin_count}</div>
                                    <div class="stats-card__label">Places Visited</div>
                                </div>

                                <div class="stats-card half">
                                    <div class="stats-card__value">${this.activatedChallenge.point_count}</div>
                                    <div class="stats-card__label">Points Earned</div>
                                </div>
                            </div>
                            ${topicData.isWriter ? html`
                                <div class="bonus_points">
                                    ${this.showBonusPoints || this.showManualCheckin ? html`
                                        <div style="width: 100%; text-align: center;display: flex; flex-direction: column; gap: 10px;">
                                            ${this.showBonusPoints ? html`
                                                <proxi-textfield
                                                    outlined
                                                    id="bonusPointsValue"
                                                    label="Point Value"
                                                    type="number"
                                                    >
                                                </proxi-textfield>
                                                <proxi-textfield
                                                    outlined
                                                    id="bonusPointsDescription"
                                                    label="Description"
                                                    >
                                                </proxi-textfield>
                                                <div class="action-buttons">
                                                    <form-button
                                                        size="sm"
                                                        variant="outline"
                                                        @click="${() => {this.showBonusPoints = !this.showBonusPoints;}}"
                                                        >
                                                        Cancel
                                                    </form-button>
                                                    <form-button
                                                        size="sm"
                                                        @click="${this.addBonusPoints}"
                                                        >
                                                        Add Bonus Points
                                                    </form-button>
                                                </div>
                                            ` : this.showManualCheckin ? html`
                                                    <proxi-selectfield
                                                        outlined
                                                        label="Select Location"
                                                        id="manual_location"
                                                        >
                                                        ${challengeData.challengeLocations.map((location) => html`
                                                            <mwc-list-item value="${location.challenge_location_info?._id?.$oid}">
                                                                ${location.point_info?.name ?? location.challenge_location_info?.location?.search ?? ''}
                                                            </mwc-list-item>
                                                        `)}
                                                    </proxi-selectfield>
                                                    <div style="display: flex; gap: 10px;padding:10px;">
                                                        <vaadin-date-picker
                                                            id="overrideDate"
                                                            style="width: 40%;padding-top: 0px;"
                                                            label="Check In Date"
                                                            value=${this.nowDate}>
                                                        </vaadin-date-picker>
                                                        <vaadin-time-picker
                                                            id="overrideTime"
                                                            style="width: 40%;padding-top: 0px;"
                                                            step="30"
                                                            label="Check In Time"
                                                            value=${this.nowTime}>
                                                        </vaadin-time-picker>
                                                    </div>
                                                    <div class="action-buttons">
                                                        <form-button
                                                            size="sm"
                                                            variant="outline"
                                                            @click="${() => {this.showManualCheckin = !this.showManualCheckin;}}"
                                                            >
                                                            Cancel
                                                        </form-button>
                                                        <form-button
                                                            size="sm"
                                                            @click="${this.addManualCheckin}"
                                                            >
                                                            Add Check In
                                                        </form-button>
                                                    </div>
                                                ` : ''}
                                        </div>
                                    ` : html`
                                        <div class="action-buttons">
                                            ${challengeData.isType(ChallengeType.Guide) ? '' : html`
                                                <form-button
                                                    size="sm"
                                                    variant="outline"
                                                    @click="${() => {this.showBonusPoints = !this.showBonusPoints;}}"
                                                    >
                                                    Add Bonus Points
                                                </form-button>
                                            `}
                                            ${challengeData.challenge?.is_live ?? false ? '' : html`
                                                <form-button
                                                    variant="outline"
                                                    size="sm"
                                                    @click="${this.toggleTesterStatus}"
                                                    >
                                                    ${this.activatedChallenge.is_tester ?? false ? 'Remove Tester Status' : 'Make Tester'}
                                                </form-button>
                                            `}
                                            ${this.activatedChallenge.link_set?.activated_challenge_page_write_link ? html`
                                                <form-button
                                                    variant="outline"
                                                    size="sm"
                                                    @click="${this.resendWelcomeEmail}"
                                                    >
                                                    Resend Secret Link
                                                </form-button>
                                            ` : ''}
                                            ${challengeData.isType(ChallengeType.Guide) ? '' : html`
                                                <form-button
                                                    size="sm"
                                                    variant="outline"
                                                    @click="${() => {this.showManualCheckin = !this.showManualCheckin;}}"
                                                    >
                                                    Add Manual Check In
                                                </form-button>
                                            `}
                                        </div>
                                    `}
                                </div>
                            ` : ''}
                            ${this.purchases?.transaction_history && this.purchases.transaction_history.length > 0
                                ? html`
                                    ${this.purchases.organization_subscriptions.length > 0
                                        ? html`
                                            <div class="sub-title">Subscription</div>
                                            <div class="subscriptions__wrapper">
                                                ${this.purchases.organization_subscriptions.map((subscription) => html`
                                                    <div class="subscriptions__card">
                                                        <div class="subscriptions__label">
                                                            Status: ${subscription.status} <br>
                                                            See More Details in the "Payments" Tab
                                                        </div>
                                                    </div>
                                                `)}
                                            </div>
                                        ` : ''}
                                    <div class="sub-title">Payments</div>
                                    ${this.purchases.transaction_history.map((transaction) => html`
                                        <div class="payments__card">
                                            <div class="payments__label">
                                                Amount: ${(transaction.total_price_cents / 100).toFixed(2)} ${transaction.currency} <br>
                                                Status: ${transaction.status} <br>
                                                Transaction ID: ${transaction.pending_transaction_id?.$oid} <br>
                                                Date: ${isDefined(transaction.payment_complete_at) ? format(new Date(transaction.payment_complete_at), 'MMM dd, yyyy @ h:mm a') : 'N/A'}
                                            </div>
                                        </div>
                                    `)}
                                ` : this.activatedChallenge?.payment_details?.payment_status === ChargebeeTransactionStatus.AdminOverridden ? html`
                                        <div class="payment__status success" style="display: flex; flex-direction:column;align-items: center; text-align: center;">
                                            <mwc-icon>check_circle</mwc-icon>
                                            Admin Overrode Payment Requirement for This User <br>
                                        </div>
                                `: challengeData.isPaymentRequired ? html`
                                    <form-button
                                        variant="outline"
                                        size="sm"
                                        @click="${this.overridePayment}"
                                        >Override Payment Requirement for This User
                                    </form-button>
                                ` : ''}
                            ${this.activatedChallenge.earned_rewards.length > 0
                                ? html`
                                    <div class="sub-title">Awards</div>

                                    <div class="awards__wrapper">
                                        ${this.activatedChallenge.earned_rewards.map((reward) => html`
                                            <div class="awards__card">
                                                <img
                                                    src="${reward.image_url}"
                                                    class="award-icon"
                                                    alt="${reward.title}"
                                                />
                                                <div class="awards__label">${reward.title}</div>
                                                ${isDefined(reward.bonus_points) && reward.bonus_points > 0 ? html`
                                                    <p class="award-bonus-points">+${reward.bonus_points} pts</p>
                                                ` : ''}
                                                ${this.imagesForLocation(reward._id?.$oid).map((img) => this.renderPhotoLink(img, 'View Photo'))}
                                                ${reward.redeemable ? html`
                                                        ${((this.activatedChallenge?.redeemed_rewards?.some(redemption =>
                                                            redemption._id.$oid === reward?._id?.$oid
                                                        )) ?? false)
                                                        ? html`
                                                            <div style="display: flex; align-items: center; gap: 4px; justify-content: center;">
                                                                <b>Redeemed</b>
                                                                <mwc-icon
                                                                    style="--mdc-icon-size: 12px; color: var(--main-moon-color); cursor: pointer;"
                                                                    @click="${async () => await this.removeRedemption(reward._id?.$oid ?? '')}"
                                                                    >delete</mwc-icon>
                                                            </div>
                                                        ` : html`
                                                            <div>
                                                                <form-button
                                                                    variant="outline"
                                                                    size="xs"
                                                                    @click="${async () => await this.redeemReward(reward._id?.$oid ?? '')}"
                                                                    icon="redeem"
                                                                >Mark Redeemed</form-button>
                                                            </div>
                                                        `}
                                                ` : ''}
                                            </div>
                                        `)}
                                    </div>`
                                : ''
                            }

                            ${this.checkinsInfo.length > 0
                                ? html`
                                    <div class="sub-title">Check Ins</div>
                                    <div class="checkins">
                                        ${this.checkinsInfo.map((checkin) => html`
                                            <div class="checkins-row">
                                                <img
                                                    src="${checkin.categoryIcon}"
                                                    class="participant-logo"
                                                    alt="${checkin.categoryName}"
                                                />
                                                <div class="checkin-detail">
                                                    <div class="place-name">
                                                        ${checkin.placeName}
                                                    </div>
                                                    ${isDefined(checkin.all_timestamps) ? html`
                                                        <div class="place-name">
                                                            <i>
                                                            ${checkin.all_timestamps.map((timestamp) =>
                                                                html`
                                                                <mwc-icon style="--mdc-icon-size: 12px; color: var(--main-moon-color); cursor: pointer;" @click="${async () => await this.removeUserCheckin(checkin, timestamp)}">delete</mwc-icon>
                                                                <span style="${!checkin.counted_timestamps.includes(timestamp) ? 'color: var(--main-moon-color);' : ''}"
                                                                >${format(new Date(timestamp), 'MMM dd, yyyy @ h:mm a')}</span><br>
                                                                ${this.imagesForLocation(checkin.challenge_location_id?.$oid).map((img) => this.renderPhotoLink(img, 'View Photo'))}
                                                                `
                                                            )}
                                                        </i></div>
                                                    ` : ''}
                                                    ${challengeData.challenge?.enable_receipt_checkin === true && checkin.receipts.length > 0 ? html`
                                                        <div class="place-name">
                                                            <i>
                                                            Receipts:
                                                            ${checkin.receipts.map((receipt) =>
                                                                html`
                                                                <a href="${receipt.image_url}" target="_blank">${formatPrice(receipt.total_spend_in_cents, receipt.currency)} [View Receipt]</a><br>
                                                                `
                                                            )}
                                                        </i></div>
                                                        ` : ''}
                                                </div>
                                                <div class="checkin-point">
                                                    ${checkin.pointValue * (checkin.counted_timestamps.length ?? 1)} Point${(checkin.counted_timestamps.length ?? 1) > 1 ? 's' : ''}
                                                    ${checkin.counted_timestamps.length > 1 ? html`
                                                        <br>
                                                        (${checkin.counted_timestamps.length} valid check ins worth ${checkin.pointValue} point${checkin.pointValue > 1 ? 's' : ''})
                                                    ` : ''}
                                                </div>
                                            </div>`
                                        )}
                                    </div>
                                `
                                : ''

                            }
                            ${this.savedPlacesInfo.length > 0
                                ? html`
                                    <div class="sub-title">Saved Places</div>
                                    <div class="checkins">
                                        ${this.savedPlacesInfo.map((place) => html`
                                            <div class="checkins-row">
                                                <img
                                                    src="${place.categoryIcon}"
                                                    class="participant-logo"
                                                    alt="${place.categoryName}"
                                                />
                                                <div class="checkin-detail">
                                                    <div class="place-name">
                                                        ${place.placeName}
                                                    </div>
                                                </div>
                                            </div>`
                                        )}
                                    </div>
                                `
                                : ''
                            }
                            ${this.activatedChallenge?.activities && this.activatedChallenge?.activities.length > 0
                                ? html`
                                    <div class="sub-title">Activity Points</div>
                                    <div class="checkins">
                                        ${this.activatedChallenge?.activities.map((point) =>  html`
                                            <div class="checkins-row">
                                                <form-button variant="outline" size="xs" @click="${async () => await this.removeUserActivity(point)}" icon="delete"></form-button>
                                                <img
                                                    src="https://img.icons8.com/?size=48&amp;id=QbIcwOBghArh&amp;format=png"
                                                    class="participant-logo"
                                                    alt="${challengeData.challenge?.activities?.find((p) => p._id?.$oid === point.activity_id.$oid)?.name ?? ''}"
                                                />
                                                <div class="checkin-detail">
                                                    <div class="place-name">
                                                        ${challengeData.challenge?.activities?.find((p) => p._id?.$oid === point.activity_id.$oid)?.name ?? ''} ${point.approved ? '' : '(Pending Approval)'}
                                                        <br>
                                                        ${(() => {
                                                            const submissionType = challengeData.challenge?.activities?.find((p) => p._id?.$oid === point.activity_id.$oid)?.submission_type;
                                                            if (submissionType === 'text') {
                                                                return point.submission.map((submission) => html`<div>${submission}</div>`);
                                                            }
                                                            if (submissionType === 'photos') {
                                                                const photos = this.imagesForLocation(point.activity_id.$oid);
                                                                if (photos.length > 0) {
                                                                    return photos.map((img) => html`${this.renderPhotoLink(img, 'View Photo')}<br>`);
                                                                }
                                                            }
                                                            return point.submission.map((submission, index) => html`<a href="${submission}" target="_blank" rel="noopener noreferrer">View Photo ${index + 1}</a><br>`);
                                                        })()}
                                                    </div
                                                    >
                                                    ${challengeData.challenge?.activities?.find((p) => p._id?.$oid === point.activity_id.$oid)?.submission_type === 'receipt' ? html`
                                                        <div class="place-name">
                                                            Receipts:
                                                            ${this.activatedChallenge?.receipts?.filter((receipt) => receipt.challenge_location_id.$oid === point.activity_id?.$oid).map((receipt) =>
                                                                html`
                                                                <a href="${receipt.image_url}" target="_blank">${formatPrice(receipt.total_spend_in_cents, receipt.currency)} [View Receipt]</a><br>
                                                                `
                                                            )}
                                                        </div>
                                                    ` : ''}
                                                </div>
                                                <div class="checkin-point">
                                                    ${challengeData.challenge?.activities?.find((p) => p._id?.$oid === point.activity_id.$oid)?.point_value ?? 0} ${challengeData.challenge?.activities?.find((p) => p._id?.$oid === point.activity_id.$oid)?.point_value === 1 ? 'Point' : 'Points'}
                                                </div>
                                            </div>`
                                        )}
                                    </div>
                                `
                                : ''
                            }
                            ${this.activatedChallenge?.admin_assigned_points && this.activatedChallenge?.admin_assigned_points.length > 0
                                ? html`
                                    <div class="sub-title">Admin Assigned Points</div>
                                    <div class="checkins">
                                        ${this.activatedChallenge?.admin_assigned_points.map((point) =>  html`
                                            <div class="checkins-row">
                                                <form-button variant="outline" size="xs" @click="${async () => await this.removeBonusPoints(point._id.$oid)}" icon="delete"></form-button>
                                                <img
                                                    src="https://img.icons8.com/?size=48&amp;id=QbIcwOBghArh&amp;format=png"
                                                    class="participant-logo"
                                                    alt="${point.description}"
                                                />
                                                <div class="checkin-detail">
                                                    <div class="place-name">
                                                        ${point.description}
                                                    </div>
                                                    ${isDefined(point.created_at) ? html`
                                                        <div class="place-name">
                                                            <i>
                                                        ${format(new Date(point.created_at), 'MMM dd, yyyy @ h:mm a')}
                                                        </i></div>
                                                    ` : ''}
                                                </div>
                                                <div class="checkin-point">
                                                    ${point.value} ${point.value === 1 ? 'Point' : 'Points'}
                                                </div>
                                            </div>`
                                        )}
                                    </div>
                                `
                                : ''
                            }
                        </div>
                    `
                    : ''
                }
                </div>
                <div class="modal__footer">
                    ${challengeData.challenge?.show_leaderboard === true ? html`
                        <form-button
                            size="xs"
                            variant="${this.activatedChallenge?.hidden_from_leaderboard === true ? 'outline' : 'outlinewarning'}"
                            @click="${this.hideOnLeaderboard}"
                            >
                            <mwc-icon>${this.activatedChallenge?.hidden_from_leaderboard === true ? 'visibility' : 'visibility_off'}</mwc-icon>
                            ${this.activatedChallenge?.hidden_from_leaderboard === true ? 'Show On Leaderboard' : 'Hide From Leaderboard' }
                        </form-button>
                    ` : ''}
                    <form-button
                        size="xs"
                        variant="outlinewarning"
                        @click="${this.openResetConfirmDialog}"
                        >
                        <mwc-icon>history</mwc-icon>
                        Reset User Data
                    </form-button>
                    <form-button
                        size="xs"
                        variant="outlinewarning"
                        @click="${this.openRemoveConfirmDialog}"
                        >
                        <mwc-icon>delete</mwc-icon>
                        Remove User
                    </form-button>
                </div>
            </div>
            <error-toast id="errorToast"></error-toast>

            <mwc-dialog id="confirmDialog" heading="Confirm Removal">
                <p>Are you sure you want to remove this user? This cannot be undone.</p>
                <mwc-button slot="primaryAction" dialogAction="confirm" @click="${this.removeUser}">Remove</mwc-button>
                <mwc-button slot="secondaryAction" dialogAction="cancel">Cancel</mwc-button>
            </mwc-dialog>
            <mwc-dialog id="resetDialog" heading="Confirm Reset">
                <p>Are you sure you want to reset this user's data, including their Points, Visits, and Awards? This cannot be undone. </p>
                <mwc-button slot="primaryAction" dialogAction="confirm" @click="${this.resetUser}">Reset</mwc-button>
                <mwc-button slot="secondaryAction" dialogAction="cancel">Cancel</mwc-button>
            </mwc-dialog>
        `;
    }


    static get styles() {
        return css`
            :host {
                --modal-width: 800px;
            }

            .modalContent {
                position: relative;
                width: 100%;
                height: auto;
                overflow-y: auto;
                box-sizing: border-box;
                height: 100%;
                display: flex;
                flex-direction: column;
            }

            .modal__header {
                width: 100%;
                text-align: center;
                padding: 0px 10px 5px;
                color: #000000;
                font-size: 1rem;
                font-weight: 700;
                line-height: 100%;
                box-sizing: border-box;
            }

            .modal__content {
                padding: 5px 0 5px;
                width: 100%;
                box-sizing: border-box;
                height: calc(100% - 20px);
                flex: 1;
                overflow-y: auto;
                padding-right: 5px;
            }

                        /* width */
            .modal__content::-webkit-scrollbar {
                width: 5px;
            }

            /* Track */
            .modal__content::-webkit-scrollbar-track {
                background: var(--theme-light-scrollbar-color);
            }

            /* Handle */
            .modal__content::-webkit-scrollbar-thumb {
                background: var(--main-moon-color);
            }

            .modal__footer {
                width: 100%;
                display: flex;
                align-items: center;
                justify-content: center;
                padding: 5px 0;
                gap: 5px;
                box-sizing: border-box;
                flex-wrap: wrap;
            }

            .action-button {
                font-size: 16px;
                border-radius: 8px;
                margin: 10px 0;
                --mdc-typography-button-text-transform: capitalize;
            }

            .aqua-blue {
                --mdc-theme-primary: var(--main-secondary-color);
            }

            .participant-details {
                width: 100;
                display: flex;
                flex-direction: column;
                align-items: center;
                gap: 12px;
            }

            .participant {
                line-height: 18px;
                text-align: center;
                color: #000000;
            }

            b.participant {
                font-weight: 700;
            }

            .sub-title {
                font-size: 0.9rem;
                font-weight: 700;
                line-height: 1rem;
                text-align: center;
                color: #000000;
            }

            .stats-card-container {
                width: 100%;
                display: flex;
                align-items: center;
                justify-content: center;
                flex-wrap: wrap;
                box-sizing: border-box;
                gap: 6px;
            }

            .stats-card {
                width: 100%;
                display: flex;
                align-items: center;
                justify-content: center;
                height: 40px;
                border: 1px solid #cac4d0;
                border-radius: 12px;
                padding: 0 16px;
                box-sizing: border-box;
                gap: 4px;
            }

            .stats-card.half {
                width: 47%;
                max-width: 170px;
            }

            .payments__label {
                border: 1px solid #CAC4D0;
                border-radius: 8px;
                display: flex;
                text-align: center;
            }

            .stats-card__value {
                font-size: 24px;
                font-weight: 700;
                line-height: 130%;
                text-align: left;
                color: #000000;
            }

            .stats-card__label {
                line-height: 125%;
                text-align: center;
                color: #1d1b20;
            }

            .bonus_points {
                width: 100%;
                display: flex;
                flex-direction: column;
                align-items: center;
                gap: 10px;
            }

            .action-buttons {
                display: flex;
                gap: 10px;
                justify-content: center;
                flex-wrap: wrap;
            }

            .awards__wrapper {
                max-width: 100%;
                display: flex;
                align-items: flex-start;
                justify-content: flex-start;
                overflow-x: auto;
            }

            .awards__card {
                display: flex;
                flex-direction: column;
                align-items: center;
                padding: 0px 5px;
                gap: 5px;
                flex: 1;
            }

            .award-icon {
                width: 37px;
                height: 37px;
            }

            .awards__label {
                text-align: center;
            }

            .award-bonus-points {
                color: #4caf50;
                font-weight: 700;
                font-size: 12px;
                margin: 4px 0 0 0;
                text-align: center;
            }

            .checkins {
                width: 100%;
            }

            .checkins-row {
                padding: 4px 0px;
                gap: 16px;
                width: 100%;
                display: flex;
                align-items: center;
                justify-content: flex-start;
                border-bottom: solid 1px #CAC4D0;
                box-sizing: border-box;
            }

            .participant-logo {
                width: 24px;
                height: 24px;
            }

            .checkin-detail {
                flex: 1;
            }

            .place-name {
                font-family: Roboto;
                font-size: 0.8rem;
                line-height: 0.8rem;
                letter-spacing: 0.5px;
                text-align: left;
                color: #1D1B20;
            }

            .checkin-point {
                font-family: Roboto;
                font-size: 11px;
                font-weight: 500;
                line-height: 16px;
                letter-spacing: 0.5px;
                text-align: right;
                color: #49454F;
                max-width: 30%;
            }

            @media only screen and (max-width: 550px) {
                .modalContent {
                    width: 100%;
                    min-height: 100%;
                    max-height: 100%;
                }

                .terms__desc-wrapper {
                    max-height: 200px;
                }
            }
        `;
    }
}